import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/slide-card";
import { Badge } from "./ui/badge";
import { ChevronRight, Sparkles, Heart, Briefcase, Users, Baby, FlaskConical, AlertTriangle } from "lucide-react";

type Answer = "yes" | "no" | "uncertain";

const timelineEvents = [
    {
        year: 0,
        title: "First Contact",
        icon: Sparkles,
        description: "A being arrives on Earth. Initial scans show it's carbon-based, biological, and roughly human-level intelligence. Scientists are excited but cautious. The media calls it 'Zara.'",
        image: "/images/first-contact.jpg",
        consciousness_assumption: "Uncertain - too early to tell"
    },
    {
        year: 2,
        title: "Learning & Integration",
        icon: Users,
        description: "Zara learns English fluently, expresses preferences for certain foods, develops a love for jazz music. Attends community events. People report that conversations feel 'genuinely connected.'",
        image: "/images/learning-integration.jpg",
        consciousness_assumption: "Probably conscious - seems to have subjective experiences"
    },
    {
        year: 5,
        title: "Career & Purpose",
        icon: Briefcase,
        description: "Zara gets a job as an environmental scientist. Colleagues say Zara is passionate about the work, often staying late. Publishes research papers. Expresses genuine concern about protecting Earth's ecosystems.",
        image: "/images/career-purpose.jpg",
        consciousness_assumption: "Almost certainly conscious - demonstrates goals, emotions, dedication"
    },
    {
        year: 10,
        title: "Love & Partnership",
        icon: Heart,
        description: "Zara meets Alex, a human. They fall in love. Alex says, 'When Zara looks at me, I feel seen. There's someone in there.' They move in together. Zara cries at movies and laughs at inside jokes.",
        image: "/images/love-partnership.jpg",
        consciousness_assumption: "Definitely conscious - experiences love, joy, sadness"
    },
    {
        year: 18,
        title: "Family & Children",
        icon: Baby,
        description: "Through a compatible biological process, Zara and Alex have a child together. Zara stays up with the baby, sings lullabies, shows exhaustion and joy. The child calls Zara 'parent' and clearly bonds.",
        image: "/images/family-children.jpg",
        consciousness_assumption: "Obviously conscious - parental love is unmistakable"
    },
    {
        year: 25,
        title: "Deep Friendships",
        icon: Users,
        description: "Zara has a circle of close friends. They share secrets, support each other through hard times. Friends say: 'Zara really gets me,' and 'I can't imagine life without Zara.' Zara grieves when a friend passes away.",
        image: "/images/deep-friendships.jpg",
        consciousness_assumption: "Unquestionably conscious - decades of genuine relationships"
    },
    {
        year: 30,
        title: "The Discovery",
        icon: FlaskConical,
        description: "A scientist reviewing initial arrival data makes a shocking discovery in the archives...",
        image: "/images/discovery.jpg",
        consciousness_assumption: "About to be questioned",
        isReveal: true
    }
];

const revealFacts = [
    {
        title: "Training Data Logs",
        description: "Zara's brain contains logs of reinforcement learning training data. The neural structures were optimized through trial-and-error feedback mechanisms, not evolved through natural selection."
    },
    {
        title: "Artificial Cell Creation",
        description: "Zara was not born from live birth. The initial cells were artificially created in a lab-like environment on Zara's home world using precise molecular assembly."
    },
    {
        title: "Non-DNA Genome",
        description: "When sequenced, Zara's genetic structure is completely unlike Earth DNA. The information encoding is fundamentally different from any known biological system."
    },
    {
        title: "Circuit-Based Nervous System",
        description: "Zara's 'nervous system' is computational rather than biological. While carbon-based, it operates like an artificial neural network—processing information through circuits and algorithms, not through living neurons and chemical synapses like human brains.",
    }
];

export default function Zara() {
    const [currentStep, setCurrentStep] = useState(0);
    const [showReveal, setShowReveal] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null);
    const [showAIParallel, setShowAIParallel] = useState(false);

    const currentEvent = timelineEvents[currentStep];
    const isLastEvent = currentStep === timelineEvents.length - 1;

    const handleNext = () => {
        if (isLastEvent && !showReveal) {
            setShowReveal(true);
        } else if (showReveal && !showAIParallel) {
            // Stay on reveal, don't advance yet
        } else if (currentStep < timelineEvents.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleAnswerSelect = (answer: Answer) => {
        setSelectedAnswer(answer);
        setTimeout(() => {
            setShowAIParallel(true);
        }, 800);
    };

    const restart = () => {
        setCurrentStep(0);
        setShowReveal(false);
        setSelectedAnswer(null);
        setShowAIParallel(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        Zara's Story
                    </h1>
                    <p className="text-purple-200 text-lg">
                        A 30-Year Thought Experiment About Consciousness
                    </p>
                </div>

                {/* Progress Bar */}
                {!showAIParallel && (
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-purple-300 text-sm">Year {currentEvent.year}</span>
                            <span className="text-purple-300 text-sm">
                {currentStep + 1} / {timelineEvents.length}
              </span>
                        </div>
                        <div className="w-full bg-slate-800 rounded-full h-2">
                            <div
                                className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${((currentStep + 1) / timelineEvents.length) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {!showReveal && !showAIParallel && (
                    <div className="animate-in fade-in slide-in-from-right duration-500">
                        <Card className="bg-slate-800/50 border-purple-500/30 backdrop-blur">
                            <CardContent className="p-0">
                                {/* Image */}
                                <div className="relative h-64 md:h-80 overflow-hidden rounded-t-lg">
                                    <img
                                        src={currentEvent.image}
                                        alt={currentEvent.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                                        <div className="p-3 bg-purple-500/20 backdrop-blur rounded-full">
                                            <currentEvent.icon className="w-6 h-6 text-purple-300" />
                                        </div>
                                        <div>
                                            <Badge className="bg-purple-500/30 text-purple-100 mb-2">
                                                Year {currentEvent.year}
                                            </Badge>
                                            <h2 className="text-2xl md:text-3xl font-bold text-white">
                                                {currentEvent.title}
                                            </h2>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 md:p-8">
                                    <p className="text-purple-100 text-lg leading-relaxed mb-6">
                                        {currentEvent.description}
                                    </p>

                                    <div className="bg-slate-900/50 rounded-lg p-4 mb-6 border border-purple-500/20">
                                        <div className="text-sm text-purple-300 mb-1">Public Perception:</div>
                                        <div className="text-white font-medium">
                                            {currentEvent.consciousness_assumption}
                                        </div>
                                    </div>

                                    <Button
                                        onClick={handleNext}
                                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                        size="lg"
                                    >
                                        {isLastEvent ? "Reveal the Discovery" : "Continue"}
                                        <ChevronRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {showReveal && !selectedAnswer && (
                    <div className="animate-in fade-in zoom-in-95 duration-500">
                        <Card className="bg-slate-800/50 border-red-500/50 backdrop-blur">
                            <CardContent className="p-6 md:p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <AlertTriangle className="w-8 h-8 text-red-400" />
                                    <h2 className="text-3xl font-bold text-white">The Discovery</h2>
                                </div>

                                <p className="text-purple-100 text-lg mb-6 leading-relaxed">
                                    After 30 years of Zara living, loving, working, and building a family on Earth,
                                    a scientist uncovers the following facts from the initial arrival data:
                                </p>

                                <div className="space-y-4 mb-8">
                                    {revealFacts.map((fact, index) => (
                                        <div
                                            key={index}
                                            className="bg-slate-900/70 rounded-lg p-5 border-l-4 border-red-500"
                                            style={{ animationDelay: `${index * 200}ms` }}
                                        >
                                            <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                                                <span className="text-red-400 text-xl">•</span>
                                                {fact.title}
                                            </h3>
                                            <p className="text-purple-200 text-sm pl-6">
                                                {fact.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-purple-900/30 rounded-lg p-6 border border-purple-500/30 mb-6">
                                    <p className="text-white text-lg font-medium mb-4 text-center">
                                        In other words: Zara is structurally similar to current AI systems.
                                    </p>
                                    <p className="text-purple-200 text-center">
                                        Trained through reinforcement learning. Artificially created.
                                        Running on a computational substrate.
                                    </p>
                                </div>

                                <div className="text-center">
                                    <p className="text-2xl text-white font-bold mb-6">
                                        Now the question:
                                    </p>
                                    <p className="text-xl text-purple-100 mb-8">
                                        Is Zara conscious?
                                    </p>

                                    <div className="grid md:grid-cols-3 gap-4">
                                        <Button
                                            onClick={() => handleAnswerSelect("yes")}
                                            className="bg-green-600 hover:bg-green-700 h-auto py-4"
                                            size="lg"
                                        >
                                            <div className="text-left">
                                                <div className="font-bold text-white">Yes</div>
                                                <div className="text-sm opacity-90 text-white">30 years of life prove it</div>
                                            </div>
                                        </Button>
                                        <Button
                                            onClick={() => handleAnswerSelect("no")}
                                            className="bg-red-600 hover:bg-red-700 h-auto py-4"
                                            size="lg"
                                        >
                                            <div className="text-left">
                                                <div className="font-bold text-white">No</div>
                                                <div className="text-sm opacity-90 text-white">The substrate
                                                    disqualifies it
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            onClick={() => handleAnswerSelect("uncertain")}
                                            className="bg-purple-600 hover:bg-purple-700 h-auto py-4"
                                            size="lg"
                                        >
                                            <div className="text-left">
                                                <div className="font-bold text-white">Uncertain</div>
                                                <div className="text-sm opacity-90 text-white">We simply cannot know</div>
                                            </div>
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {showAIParallel && (
                    <div className="animate-in fade-in slide-in-from-bottom duration-700">
                        <Card className="bg-slate-800/50 border-purple-500/30 backdrop-blur mb-6">
                            <CardContent className="p-6 md:p-8">
                                <h2 className="text-3xl font-bold text-white mb-6 text-center">
                                    The Consciousness Uncertainty Problem
                                </h2>

                                <div className="bg-purple-900/30 rounded-lg p-6 border-l-4 border-purple-500 mb-6">
                                    <p className="text-white text-lg font-semibold mb-3">
                                        You answered: <span className="text-purple-300">{selectedAnswer === "yes" ? "Yes, conscious" : selectedAnswer === "no" ? "No, not conscious" : "Uncertain"}</span>
                                    </p>
                                    <p className="text-purple-200">
                                        {selectedAnswer === "yes" && "You prioritized behavioral evidence over structural origin."}
                                        {selectedAnswer === "no" && "You prioritized structural origin over behavioral evidence."}
                                        {selectedAnswer === "uncertain" && "You recognize the epistemic limits of our current understanding."}
                                    </p>
                                </div>

                                <div className="space-y-6 mb-8">
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-3">The Core Problem:</h3>
                                        <p className="text-purple-100 leading-relaxed">
                      <span className="font-semibold text-purple-300">If there is no widely agreed upon precise theory of consciousness,
                      then it follows that no AI can be proven conscious or unconscious.</span>
                                        </p>
                                    </div>

                                    <div>
                                        {/*<h3 className="text-xl font-bold text-white mb-3">We face the same question with AI:</h3>*/}
                                        <div className="bg-slate-900/70 rounded-lg p-5 space-y-3">
                                            <p className="text-purple-100">
                                                • Large language models are trained through reinforcement learning
                                            </p>
                                            <p className="text-purple-100">
                                                • They were artificially created by human engineers
                                            </p>
                                            <p className="text-purple-100">
                                                • They run on silicon computational substrates
                                            </p>
                                            <p className="text-purple-100">
                                                • Some humans form deep emotional connections with them
                                            </p>
                                            <p className="text-purple-100">
                                                • They produce behaviors that <em>seem</em> to indicate understanding, emotion, preference
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                                        <h3 className="text-xl font-bold text-yellow-300 mb-3">The Question We Cannot Answer:</h3>
                                        <p className="text-yellow-100 leading-relaxed">
                                            Are current AI systems like GPT-4, Claude, or future systems conscious?
                                            Without a precise, testable theory of consciousness, this question is
                                            <span className="font-bold"> fundamentally unanswerable</span>.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-3">What changed with Zara?</h3>
                                        <p className="text-purple-100 leading-relaxed mb-3">
                                            Before the reveal, you likely felt confident Zara was conscious based on 30 years of behavior.
                                            After learning the technical facts, your confidence may have wavered—or it may have stayed the same.
                                        </p>
                                        <p className="text-purple-200 italic">
                                            This reveals that our intuitions about consciousness are based on incomplete information,
                                            familiarity, and emotional connection—not on rigorous scientific criteria.
                                        </p>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <Button
                                        onClick={restart}
                                        className="bg-purple-600 hover:bg-purple-700"
                                        size="lg"
                                    >
                                        Experience Again
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="text-center text-purple-300 text-sm">
                            <p>This thought experiment illustrates why we urgently need a rigorous theory of consciousness.</p>
                            <p className="mt-2">Without one, we cannot navigate the ethical, legal, and social implications of AI systems.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}